import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { BadgePercent, Check, X } from "lucide-react";
import { useGetMarketDataHistory } from "@/api/securities/useGetMarketDataHistory";
import { Button } from "@/components/Button/Button";
import { formatNumber } from "@/utils/number";

export type PriceRow = {
  tier: string;
  note?: string;
  active: string;
  ekb: string;
  ekbNote?: string;
  realEstate: string;
  realEstateNote?: string;
  bonds: string;
  safe: string;
};

export const PERF_FEE_COLS = [
  "Aktiv EKB",
  "Aktiv Eiendom",
  "Aktiv Aksjer",
  "Aktiv 75",
  "Aktiv 60",
  "Aktiv 45",
  "Aktiv 25",
  "Aktiv Renter",
  "Aktiv Trygg",
] as const;

export const PERF_FEE_ROWS = [
  3.7, 4.6, 4.6, 3.85, 3.4, 2.95, 2.45, 1.6, 0.0,
] as const;

export const PRICELIST_ROWS: PriceRow[] = [
  {
    tier: "<2",
    active: "1,2 %",
    ekb: "1,2 %",
    realEstate: "1,2 %",
    bonds: "1,0 %",
    safe: "0,25 %",
  },
  {
    tier: "2-5",
    active: "1 %",
    ekb: "1,1 %",
    realEstate: "1,1 %",
    bonds: "0,8 %",
    safe: "0,25 %",
  },
  {
    tier: "5-10",
    active: "0,9 %",
    ekb: "1,0 %",
    realEstate: "1,0 %",
    bonds: "0,7 %",
    safe: "0,25 %",
  },
  {
    tier: "10+",
    active: "0,8 %",
    ekb: "0,9 %",
    realEstate: "0,9 %",
    bonds: "0,5 %",
    safe: "0,25 %",
  },
  {
    tier: "Minstetegning",
    active: "-",
    ekb: "1 MNOK",
    ekbNote: "5 MNOK i finansiell formue",
    realEstate: "750 000",
    realEstateNote: "7,5 MNOK i finansiell formue",
    bonds: "-",
    safe: "-",
  },
  {
    tier: "Inn/Ut",
    active: "0 %",
    ekb: "0,5 %",
    realEstate: "0,25 %",
    bonds: "0 %",
    safe: "0 %",
  },
  {
    tier: "Kurtasje belastes",
    active: "ja",
    ekb: "nei",
    realEstate: "ja",
    bonds: "ja",
    safe: "ja",
  },
  {
    tier: "Depot",
    note: "0,15 % for aksjesparekonto",
    active: "0,18 %",
    ekb: "0,18 %",
    realEstate: "0,18 %",
    bonds: "0,18 %",
    safe: "0,18 %",
  },
];

const NIBOR_ID = 2_123_649 as const;

export function Pricelist() {
  const [open, setOpen] = useState(false);

  const { data, loading } = useGetMarketDataHistory(NIBOR_ID, new Date());
  const NIBOR = data?.security?.marketDataHistory?.[0]?.close1 ?? 0;

  const renderCellValue = (value: string, isBooleanRow: boolean) => {
    if (!isBooleanRow) {
      const raw = value.replace("%", "").trim().replace(",", ".");
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return `${formatNumber(parsed, 2)} %`;
      }
      return value;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized === "ja") {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <Check className="size-4" aria-hidden="true" />
          <span>ja</span>
        </span>
      );
    }

    if (normalized === "nei") {
      return (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
          <X className="size-4" aria-hidden="true" />
          <span>nei</span>
        </span>
      );
    }

    return value;
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        icon={<BadgePercent size={16} />}
      >
        Pricelist
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <div className="absolute top-3 right-3">
          <Button variant="icon" size="sm" onClick={() => setOpen(false)}>
            <X className="size-4" />
          </Button>
        </div>
        <DialogTitle className="text-xl">Price List</DialogTitle>
        <DialogContent className="pt-2 flex flex-col gap-4">
          {/* Prisliste */}
          <div className="rounded-2xl border bg-gradient-to-b from-muted/40 to-background p-3 sm:p-4">
            <div className="hidden sm:grid grid-cols-6 gap-2 px-3 py-2 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
              <div>MNOK</div>
              <div className="text-right">Aktiv</div>
              <div className="text-right">EKB</div>
              <div className="text-right">Eiendom</div>
              <div className="text-right">Renter</div>
              <div className="text-right">Trygg</div>
            </div>

            <div className="space-y-2">
              {PRICELIST_ROWS.map((row) => {
                const isBooleanRow = row.tier === "Kurtasje belastes";

                return (
                  <div
                    key={row.tier}
                    className="rounded-xl border border-border/70 bg-background/80 px-3 py-3 sm:py-2 sm:grid sm:grid-cols-6 sm:gap-2 sm:items-center"
                  >
                    <div className="font-semibold text-foreground mb-2 sm:mb-0">
                      <div>{row.tier}</div>
                      {row.note ? (
                        <div className="mt-1 text-xs font-normal text-muted-foreground leading-snug">
                          {row.note}
                        </div>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 gap-y-1 text-sm sm:contents">
                      <div className="text-muted-foreground sm:hidden text-right">
                        Aktiv
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.active, isBooleanRow)}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        EKB
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        <div>{renderCellValue(row.ekb, isBooleanRow)}</div>
                        {row.ekbNote ? (
                          <div className="mt-0.5 text-[12px] font-normal leading-snug text-muted-foreground">
                            {row.ekbNote}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Eiendom
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        <div>
                          {renderCellValue(row.realEstate, isBooleanRow)}
                        </div>
                        {row.realEstateNote ? (
                          <div className="mt-0.5 text-[12px] font-normal leading-snug text-muted-foreground">
                            {row.realEstateNote}
                          </div>
                        ) : null}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Renter
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.bonds, isBooleanRow)}
                      </div>

                      <div className="text-muted-foreground sm:hidden text-right">
                        Trygg
                      </div>
                      <div className="tabular-nums text-foreground font-medium text-right">
                        {renderCellValue(row.safe, isBooleanRow)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Performance Fee */}
          <div className="rounded-2xl border bg-gradient-to-b from-muted/40 to-background p-3 sm:p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 px-1">
              Performance Fee
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-y-1">
                <thead>
                  <tr>
                    <th className="text-left px-3 pb-1 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap" />
                    {PERF_FEE_COLS.map((col) => (
                      <th
                        key={col}
                        className="text-right px-3 pb-1 text-xs uppercase tracking-wide font-semibold text-muted-foreground whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* NIBOR 3M — same value across all columns */}
                  <tr className="rounded-xl">
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-background/80 border border-r-0 border-border/70">
                      NIBOR 3M
                    </td>
                    {PERF_FEE_COLS.map((_, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-medium text-foreground bg-background/80 border-y border-border/70" +
                          (index === PERF_FEE_COLS.length - 1
                            ? " rounded-r-xl border-r border-border/70"
                            : "")
                        }
                      >
                        {loading ? "…" : `${formatNumber(NIBOR, 2)} %`}
                      </td>
                    ))}
                  </tr>
                  {/* Risikopremie */}
                  <tr className="rounded-xl">
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-background/80 border border-r-0 border-border/70">
                      Risikopremie
                    </td>
                    {PERF_FEE_ROWS.map((row, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-medium text-foreground bg-background/80 border-y border-border/70" +
                          (index === PERF_FEE_ROWS.length - 1
                            ? " rounded-r-xl border-r border-border/70"
                            : "")
                        }
                      >
                        {formatNumber(row, 2)} %
                      </td>
                    ))}
                  </tr>
                  {/* Hurdle (NIBOR + risikopremie) — highlighted */}
                  <tr>
                    <td className="px-3 py-2 font-semibold text-foreground whitespace-nowrap rounded-l-xl bg-primary/5 border border-r-0 border-primary/20">
                      Hurdle
                    </td>
                    {PERF_FEE_ROWS.map((row, index) => (
                      <td
                        key={index}
                        className={
                          "px-3 py-2 text-right tabular-nums font-semibold text-foreground bg-primary/5 border-y border-primary/20" +
                          (index === PERF_FEE_ROWS.length - 1
                            ? " rounded-r-xl border-r border-primary/20"
                            : "")
                        }
                      >
                        {loading ? "…" : `${formatNumber(row + NIBOR, 2)} %`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Pricelist;
